import { Injectable } from '@angular/core';

/**
 * Resolves "click a notification → go to its detail page" (2026-08-06). Each
 * event_key's recipient is fixed by sportiq-notify's manifest (see
 * sportiq-licence/notify.manifest.js) — submitted/cancelled_by_club always go to
 * FED_ADMIN, validated/rejected/cancelled_by_fede/status-changes always go to
 * CLUB_MANAGER — so the route can be picked from the event key alone, no need to
 * check the current user's role here.
 *
 * Two real traps found while mapping this (confirmed against the actual backend
 * controllers, not guessed from param names):
 *  - joueurs/dirigeants/staff/arbitres notifications carry the ENTITY-level fed_seq
 *    (player_fed_seq, etc.), not the relation/junction-level one — so they must go
 *    to the top-level capitalized routes (/Joueurs, /Dirigeants, /Staff, /Arbitres),
 *    NOT /club/licences/... (which resolves a different, relation-level fed_seq the
 *    payload doesn't have). Those routes' relationId param is optional and safely
 *    dropped when falsy, so '0' is a safe placeholder.
 *  - there is no club-side arbitres detail route at all — referees are always
 *    federation-linked in practice (submitDemande forces this), so a club-recipient
 *    demande event should never actually carry entity_type: 'referee'; falls back
 *    to the demandes list if it ever does.
 *
 * Two events (seasons:status_changed, licenceperiod:closing) have no detail page at
 * all in this app today — only a list page exists, so that's the best available
 * target rather than a dead link.
 */
const ENTITY_TYPE_TO_ROUTE_SEGMENT: Record<string, string> = {
  player: 'joueurs',
  manager: 'dirigeants',
  staff: 'staff',
  referee: 'arbitres',
  club: 'clubs',
};

@Injectable({ providedIn: 'root' })
export class NotificationRouteService {
  routeFor(eventKey: string, payload: Record<string, any> | null | undefined): string[] | null {
    const parts = eventKey.split(':');
    const model = parts[1];
    const action = parts[2];
    const p = payload || {};

    switch (`${model}:${action}`) {
      case 'demande:submitted':
      case 'demande:cancelled_by_club': {
        const segment = ENTITY_TYPE_TO_ROUTE_SEGMENT[p['entity_type']];
        if (!segment || !p['demande_fed_seq']) return null;
        return ['/federation', 'demandes', segment, String(p['demande_fed_seq'])];
      }

      case 'demande:validated':
      case 'demande:rejected':
      case 'demande:cancelled_by_fede': {
        const segment = ENTITY_TYPE_TO_ROUTE_SEGMENT[p['entity_type']];
        if (!segment || segment === 'arbitres' || !p['demande_fed_seq']) return ['/club', 'demandes'];
        return ['/club', 'demandes', segment, String(p['demande_fed_seq'])];
      }

      case 'clubs:validated':
      case 'clubs:rejected':
      case 'clubs:status_changed':
        return ['/club'];

      case 'joueurs:status_changed':
      case 'joueurs:licence_deactivated':
      case 'joueurs:licence_reactivated':
        return p['player_fed_seq'] ? ['/Joueurs', String(p['player_fed_seq']), '0'] : null;

      case 'dirigeants:status_changed':
      case 'dirigeants:licence_deactivated':
      case 'dirigeants:licence_reactivated':
        return p['manager_fed_seq'] ? ['/Dirigeants', String(p['manager_fed_seq']), '0'] : null;

      case 'staff:status_changed':
      case 'staff:licence_deactivated':
      case 'staff:licence_reactivated':
        return p['staff_fed_seq'] ? ['/Staff', String(p['staff_fed_seq']), '0'] : null;

      case 'arbitres:status_changed':
      case 'arbitres:licence_deactivated':
      case 'arbitres:licence_reactivated':
        return p['referee_fed_seq'] ? ['/Arbitres', String(p['referee_fed_seq']), '0'] : null;

      case 'seasons:status_changed':
        return ['/Saisons'];

      case 'licenceperiod:closing':
        return ['/federation', 'compagnes'];

      default:
        return null;
    }
  }
}
